import { Box } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import type { Control } from "react-hook-form"

import { COST_BREAKDOWN_CURRENCY_FIELD_PROPS, COST_BREAKDOWN_PERCENTAGE_FIELD_PROPS } from "../../constants"
import { CostBreakdownStandardField } from "../../types"
import { getCostBreakdownProductFieldPaths } from "../../utils"

import { FormNumericField } from "@/components/UiKit/FormControls"

interface AmountPercentageFieldPairProps {
  control: Control<any>
  productIndex: number
  fieldPrefix: CostBreakdownStandardField | "tariffs"
  label: string
  size?: "small" | "medium"
  arrayIndex?: number
  isDisabled?: boolean
}

export const AmountPercentageFieldPair: FC<AmountPercentageFieldPairProps> = ({
  control,
  productIndex,
  fieldPrefix,
  label,
  size = "small",
  arrayIndex,
  isDisabled = false,
}) => {
  const fieldPaths = useMemo(() => getCostBreakdownProductFieldPaths(productIndex), [productIndex])

  const isArrayIndexExist = arrayIndex !== null && arrayIndex !== undefined

  // Build field paths to match exactly what the centralized utility and hooks expect
  let amountPath: string
  let percentagePath: string

  if (isArrayIndexExist) {
    // For array fields like tariffs
    amountPath = `${fieldPaths.tariffs}.${arrayIndex}.amount`
    percentagePath = `${fieldPaths.tariffs}.${arrayIndex}.percentage`
  } else {
    // For object fields like duties, mpf, hmf - use centralized paths
    switch (fieldPrefix) {
      case CostBreakdownStandardField.Duties:
        amountPath = fieldPaths.duties.amount
        percentagePath = fieldPaths.duties.percentage
        break
      case CostBreakdownStandardField.Mpf:
        amountPath = fieldPaths.mpf.amount
        percentagePath = fieldPaths.mpf.percentage
        break
      case CostBreakdownStandardField.Hmf:
        amountPath = fieldPaths.hmf.amount
        percentagePath = fieldPaths.hmf.percentage
        break
      default:
        throw new Error(`Unknown fieldPrefix: ${fieldPrefix}`)
    }
  }

  return (
    <Box className='flex items-start gap-2'>
      <FormNumericField
        control={control}
        name={amountPath}
        label={label}
        size={size}
        disabled={isDisabled}
        data-testid={`product-${productIndex}-${fieldPrefix}${isArrayIndexExist ? `-${arrayIndex}` : ""}-amount-input`}
        {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
        sx={{
          "& .MuiInputLabel-root": {
            whiteSpace: "nowrap",
            overflow: "visible",
            maxWidth: "none",
            width: "auto",
          },
          "& .MuiFormHelperText-root": {
            position: "absolute",
            bottom: "-20px",
            whiteSpace: "nowrap",
          },
        }}
      />
      <span className='mt-7 text-gray-500'>=</span>
      <FormNumericField
        control={control}
        name={percentagePath}
        size={size}
        disabled={isDisabled}
        data-testid={`product-${productIndex}-${fieldPrefix}${isArrayIndexExist ? `-${arrayIndex}` : ""}-percentage-input`}
        {...COST_BREAKDOWN_PERCENTAGE_FIELD_PROPS}
        sx={{
          alignSelf: "end",
        }}
      />
    </Box>
  )
}
